import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  selectCurrentTeam,
  selectCurrentTeamCustomersPermissions,
  selectCurrentTeamInvoicePermissions,
  selectMerchantTeams,
  setCurrentTeam,
  showNotifier
} from 'containers/app/appSlice';
import { usePaginatedGetInvoicesV2Query } from './hooks/usePaginatedGetInvoicesV2Query';
import { formatInvoiceFilterParamsForAPI } from './utils';
import { CustomButton, If, Loader, NoPermissions, Pagination, SearchWithDropdown } from 'components';
import { debounce } from 'utils/commonUtils';
import InvoicesTableV2 from './components/InvoicesTableV2';
import { useAppDispatch, useAppSelector } from 'hooks';
import { InvoicesPageIcon, InvoicesTabIcon, NoSearchResultsIcon, SearchIcon } from 'assets/icons';
import { useGetInvoiceSearchSuggestionsQuery, useLazyExportInvoicesQuery } from './api';
import CustomizeColumnsModal, { ColumnConfig } from 'components/customize-columns-modal/CustomizeColumnsModal';
import { useSaveCustomColumnsMutation } from 'containers/accounting/pay-runs/api';
import { InvoiceStatus, InvoiceV2ColumnInfo } from './invoices.model';
import InvoiceTabs from './components/InvoiceTabs';
import InvoicesFilter from './components/InvoicesFilter';
import InvoicesTableActions from './components/InvoicesTableActions';
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import routesPath from 'routes/RoutesPath';
import { InvoicesRouteState } from 'routes/types';
import { NotifierTypes } from 'constants/notifierConstants';
import { selectInvoiceData, selectPaymentStatus, updatePaymentStatus } from '../payments-side-panel/sidePanelSlice';
import ShareInvoiceModal from './create-invoice/components/ShareInvoiceModal';
import { resetInvoiceState } from './InvoicesSlice';
import { CleverTapEventsInvoices } from './events';
import { logAnalyticEvent } from 'utils/analytics';
import { InfoMesssages } from 'types/infoMessages';
import { ROOT_ROUTE_STATE } from 'routes/constants';
import { INVOICE_SEARCH_TYPES, INVOICE_TAB_STATUS, INVOICE_TAB_TITLE } from './constants';
import { ButtonType } from 'components/custom-button/CustomButton';

const ITEMS_PER_PAGE = 50;
const PAGE_SIZE_STORAGE_KEY = 'scanpay-invoice-page-size';

const Invoices: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { rootInvoiceId, teamId: teamIdFromUrl } = useParams();
  const [searchParams] = useSearchParams();

  const invoicePermissions = useAppSelector(selectCurrentTeamInvoicePermissions);
  const customerPermissions = useAppSelector(selectCurrentTeamCustomersPermissions);
  const invoiceData = useAppSelector(selectInvoiceData);
  const invoicePaymentStatus = useAppSelector(selectPaymentStatus);
  const currentTeam = useAppSelector(selectCurrentTeam);
  const merchantTeams = useAppSelector(selectMerchantTeams);

  const getInitialPageSize = (): number => {
    try {
      const storedPageSize = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
      if (storedPageSize) {
        const parsed = parseInt(storedPageSize, 10);
        if (!isNaN(parsed) && parsed > 0) {
          return parsed;
        }
      }
    } catch (error) {
      //
    }
    return ITEMS_PER_PAGE;
  };

  const initialPageSize = getInitialPageSize();

  const [searchText, setSearchText] = useState('');
  const [searchType, setSearchType] = useState(INVOICE_SEARCH_TYPES[0]);
  const [currentTab, setCurrentTab] = useState(INVOICE_TAB_TITLE.ALL_INVOICES);
  const [invoiceShareModalInfo, setInvoiceShareModalInfo] = useState<{ show: boolean; isEditInvoiceShare: boolean }>({
    show: false,
    isEditInvoiceShare: false
  });
  const [showEditTableModal, setShowEditTableModal] = useState(false);
  const [clicked, setClicked] = useState(false);

  const [exportInvoicesQuery] = useLazyExportInvoicesQuery();
  const [saveCustomColumns, { isLoading: isSavingColumns }] = useSaveCustomColumnsMutation();
  const { data: suggestionsResponse } = useGetInvoiceSearchSuggestionsQuery({
    teamId: currentTeam?.id
  });

  const invoiceSearchSuggestions: string[] = suggestionsResponse?.suggestion || [];

  const teamId = searchParams.get('teamId');
  const invoiceDetailsId = searchParams.get('id');

  const {
    list: invoicesList,
    isLoading: isFetchingInvoices,
    fetchData,
    resetQuery: resetInvoicesQuery,
    setQueryParams: setInvoicesQueryParams,
    queryParams: invoicesQueryParams,
    totalListCount: invoicesCount,
    statusBasedCount,
    hasPermissionsToView,
    columnConfig,
    page,
    isLoadingSuccess: isInvoicesLoadingSuccess
  } = usePaginatedGetInvoicesV2Query(initialPageSize);

  const previousTeamIdRef = useRef<number | undefined>(currentTeam?.id);
  const isTeamSwitchFromRouteRef = useRef(false);
  const previousPermissionsRef = useRef<{
    viewInvoicesByTeam: boolean;
    viewInvoicesBySelf: boolean;
  }>({
    viewInvoicesByTeam: invoicePermissions.viewInvoicesByTeam,
    viewInvoicesBySelf: invoicePermissions.viewInvoicesBySelf
  });
  const routeStateFiltersRef = useRef<{ customerId?: string[]; createdBy?: string[] } | null>(null);

  useEffect(() => {
    const routeState = location.state as InvoicesRouteState;

    if (routeState?.showShareModal) {
      setInvoiceShareModalInfo({
        show: routeState?.showShareModal,
        isEditInvoiceShare: !!routeState?.isEditInvoiceShare
      });
      navigate(routesPath.INVOICES, { replace: true, state: { showShareModal: false, isEditInvoiceShare: false } });
    }

    if (routeState?.pendingInvoicesTab) {
      setCurrentTab(INVOICE_TAB_TITLE.OPEN);
      setInvoicesQueryParams(prevQueryParams => ({
        ...prevQueryParams,
        groupingStatus: INVOICE_TAB_STATUS.OPEN
      }));
      navigate(routesPath.INVOICES, { replace: true, state: { pendingInvoiceTab: false } });
    }
    if (routeState?.merchantId) {
      if (!routeStateFiltersRef.current) {
        routeStateFiltersRef.current = {};
      }
      routeStateFiltersRef.current.createdBy = [routeState.merchantId];

      setInvoicesQueryParams(prevQueryParams => ({
        ...prevQueryParams,
        createdBy: [routeState.merchantId]
      }));
      navigate(routesPath.INVOICES, { replace: true, state: { merchantId: null } });
    }

    if (routeState?.customerId) {
      if (!routeStateFiltersRef.current) {
        routeStateFiltersRef.current = {};
      }
      routeStateFiltersRef.current.customerId = [routeState.customerId];

      setInvoicesQueryParams(prevQueryParams => ({
        ...prevQueryParams,
        customerId: [routeState.customerId]
      }));
      navigate(routesPath.INVOICES, { replace: true, state: { customerId: null } });
    }

    const teamIdVal = teamIdFromUrl ? teamIdFromUrl : teamId;
    if (teamIdVal && currentTeam?.id !== +teamIdVal) {
      const merchantTeam = merchantTeams.find(team => team.id === +teamIdVal);
      if (merchantTeam) {
        isTeamSwitchFromRouteRef.current = true;
        dispatch(setCurrentTeam(merchantTeam));
        dispatch(resetInvoiceState());
        resetInvoicesQuery();
        setCurrentTab(INVOICE_TAB_TITLE.ALL_INVOICES);
        setInvoicesQueryParams(prevQueryParams => ({
          ...prevQueryParams,
          groupingStatus: INVOICE_TAB_STATUS.ALL_INVOICES
        }));
      } else {
        navigate(routesPath.INVOICES, { replace: true });
      }
    }
  }, [location, currentTeam?.id, merchantTeams, teamIdFromUrl, teamId]);

  useEffect(() => {
    if (currentTeam?.id && previousTeamIdRef.current !== currentTeam?.id) {
      if (!isTeamSwitchFromRouteRef.current) {
        resetInvoicesQuery();
        setCurrentTab(INVOICE_TAB_TITLE.ALL_INVOICES);
        setInvoicesQueryParams(prevQueryParams => ({
          ...prevQueryParams,
          groupingStatus: INVOICE_TAB_STATUS.ALL_INVOICES
        }));
      }
      isTeamSwitchFromRouteRef.current = false;
      routeStateFiltersRef.current = null;
      previousTeamIdRef.current = currentTeam?.id;
    }
  }, [currentTeam?.id, resetInvoicesQuery]);

  useEffect(() => {
    const currentViewInvoicesByTeam = invoicePermissions.viewInvoicesByTeam;
    const currentViewInvoicesBySelf = invoicePermissions.viewInvoicesBySelf;
    const previousViewInvoicesByTeam = previousPermissionsRef.current.viewInvoicesByTeam;
    const previousViewInvoicesBySelf = previousPermissionsRef.current.viewInvoicesBySelf;

    if (
      currentViewInvoicesByTeam !== previousViewInvoicesByTeam ||
      currentViewInvoicesBySelf !== previousViewInvoicesBySelf
    ) {
      resetInvoicesQuery();
      previousPermissionsRef.current = {
        viewInvoicesByTeam: currentViewInvoicesByTeam,
        viewInvoicesBySelf: currentViewInvoicesBySelf
      };
    }
  }, [invoicePermissions.viewInvoicesByTeam, invoicePermissions.viewInvoicesBySelf, resetInvoicesQuery]);

  useEffect(() => {
    if (
      ((invoicePaymentStatus && invoiceData?.invoiceStatus !== invoicePaymentStatus) ||
        invoiceData?.invoiceStatus === InvoiceStatus.PAID ||
        invoiceData?.invoiceStatus === InvoiceStatus.PAID_QR) &&
      !rootInvoiceId
    ) {
      resetInvoicesQuery();
      dispatch(updatePaymentStatus(''));
    }
  }, [invoiceData, invoicePaymentStatus, rootInvoiceId]);

  useEffect(() => {
    if (invoiceDetailsId) {
      navigate(routesPath.INVOICES, { replace: true });
      handleInvoiceClick(invoiceDetailsId);
    }
  }, [invoiceDetailsId]);

  const handleSearchIconClick = () => {
    setClicked(true);
  };

  const onTabClick = tab => {
    setCurrentTab(tab.title);
    handleSidePanelClose();
    setInvoicesQueryParams(prevQueryParams => ({ ...prevQueryParams, groupingStatus: tab.value }));
  };

  const handleSidePanelClose = () => {
    navigate(routesPath.INVOICES);
  };

  const handleInvoiceClick = useCallback(
    (invoiceId: string, invoiceStatus = '') => {
      dispatch(updatePaymentStatus(invoiceStatus));
      navigate(`/invoices/${currentTeam.id}/${invoiceId}/invoice`, { state: ROOT_ROUTE_STATE });
    },
    [currentTeam.id, dispatch, navigate]
  );

  const handleInvoiceCreation = (id: string) => {
    logAnalyticEvent(CleverTapEventsInvoices.webInvoicesCreateInvoice);
    if (!invoicePermissions.createInvoice || !customerPermissions.createNewCustomers) {
      onInfoClick(id, InfoMesssages.createInvoiceNoPermissionMessage);
      return;
    }
    if (invoicePermissions.createInvoice) {
      dispatch(resetInvoiceState());
      navigate(routesPath.INVOICES_CREATE);
    }
  };

  const initiateSearch = useCallback(
    (searchTerm?: string) => {
      if (!searchTerm || searchTerm.trim() === '') {
        setInvoicesQueryParams(prevQueryParams => ({
          ...prevQueryParams,
          searchKey: undefined,
          searchValue: undefined
        }));
        return;
      }

      setInvoicesQueryParams(prevQueryParams => ({
        ...prevQueryParams,
        searchKey: searchType.type,
        searchValue: searchTerm.trim()
      }));
    },
    [searchType.type]
  );

  const debouncedSearchHandler = useCallback(debounce(initiateSearch, 500), [initiateSearch]);

  const onSearchTermChange = (searchTerm: string) => {
    setSearchText(searchTerm);
    debouncedSearchHandler(searchTerm);
  };

  const onClearSearch = () => {
    setSearchText('');
    initiateSearch('');
  };

  const onInfoClick = (id: string, infoMessage: string) => {
    dispatch(
      showNotifier({
        id,
        message: {
          primaryMessage: infoMessage,
          secondaryMessage: '',
          isMessageHtml: true
        },
        type: NotifierTypes.WARNING,
        showClose: true,
        fontStyle: 'text-primary font-normal'
      })
    );
  };

  const handleFiltersChange = useCallback((filters: Record<string, unknown>) => {
    setInvoicesQueryParams(prevParams => ({
      ...prevParams,
      ...filters
    }));
  }, []);

  const handlePageChange = (pageNumber: number) => {
    fetchData(pageNumber);
  };

  const handleSortChangeWrapper = (sortByParam: string, sortOrderParam: string) => {
    setInvoicesQueryParams(prevParams => {
      return {
        ...prevParams,
        sortBy: sortByParam,
        sortOrder: sortOrderParam
      };
    });
  };

  const handleEditTableClick = () => {
    setShowEditTableModal(true);
  };

  const handleEditTableModalClose = () => {
    setShowEditTableModal(false);
  };

  const handleEditTableSave = async (columns: ColumnConfig[]) => {
    if (!currentTeam?.id) return;

    try {
      await saveCustomColumns({
        teamId: currentTeam.id,
        payload: {
          feature: 'INVOICES',
          columnInfo: columns.map(col => ({
            columnKey: col.columnKey,
            columnLabel: col.columnLabel,
            isEnabled: col.isEnabled,
            priority: col.priority,
            showAlways: col.showAlways,
            isFocused: col.isFocused
          }))
        }
      }).unwrap();

      handleEditTableModalClose();
    } catch (error) {
      //
    }
  };

  const handleSelectResultsPerPage = (pageSize: number) => {
    try {
      localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize.toString());
    } catch (error) {
      //
    }
    setInvoicesQueryParams(prevQueryParams => ({
      ...prevQueryParams,
      limit: pageSize
    }));
  };

  const getInvoicesColumn = useMemo(() => {
    return columnConfig.map(col => ({
      columnKey: col.columnKey,
      columnLabel: col.columnLabel,
      isEnabled: col.isEnabled,
      priority: col.priority,
      showAlways: col.showAlways,
      isFocused: col.isFocused
    }));
  }, [columnConfig]);

  const handleExport = async () => {
    if (!currentTeam?.id) return;
    dispatch(
      showNotifier({
        message: {
          primaryMessage: 'Downloading started in the background',
          secondaryMessage: ''
        },
        type: NotifierTypes.INFO,
        showClose: false,
        fontStyle: 'text-primary font-normal'
      })
    );
    const formattedParams = formatInvoiceFilterParamsForAPI(invoicesQueryParams);
    const exportParams = {
      teamId: currentTeam.id.toString(),
      ...formattedParams
    };
    await exportInvoicesQuery(exportParams).unwrap();
  };

  const handleCloseShareModal = () => {
    setInvoiceShareModalInfo({ show: false, isEditInvoiceShare: false });
  };

  const isFilterResultEmpty =
    invoicesList?.length === 0 &&
    !isFetchingInvoices &&
    !invoicesQueryParams.searchValue &&
    (invoicesQueryParams.customerId?.length > 0 ||
      invoicesQueryParams.invoiceStatus?.length > 0 ||
      invoicesQueryParams.paymentMethods?.length > 0 ||
      invoicesQueryParams.tipStatus ||
      invoicesQueryParams.createdBy?.length > 0 ||
      invoicesQueryParams.createdAtFrom ||
      invoicesQueryParams.dueDateFrom ||
      invoicesQueryParams.totalAmount ||
      invoicesQueryParams.refundStatus ||
      invoicesQueryParams.disputeStatus ||
      invoicesQueryParams.reminderStatus ||
      invoicesQueryParams.jobStatus ||
      invoicesQueryParams.estimateStatus);

  const tabCounts = useMemo(() => {
    return {
      [INVOICE_TAB_TITLE.ALL_INVOICES]: Number(statusBasedCount?.allInvoicesCount) || 0,
      [INVOICE_TAB_TITLE.OPEN]: Number(statusBasedCount?.openInvoicesCount) || 0,
      [INVOICE_TAB_TITLE.PAST_DUE]: Number(statusBasedCount?.pastDueInvoicesCount) || 0,
      [INVOICE_TAB_TITLE.PAID]: Number(statusBasedCount?.paidInvoicesCount) || 0
    };
  }, [statusBasedCount]);

  return (
    <>
      <div className="overflow-y flex h-screen flex-col justify-between pt-6">
        <div className="mb-6 flex flex-row items-center gap-3 px-6">
          <div className="text-2xl font-bold text-primaryText">Invoices</div>
          <If condition={!clicked}>
            <div onClick={handleSearchIconClick}>
              <SearchIcon className="mx-2 flex lg:hidden" />
            </div>
          </If>
          <SearchWithDropdown
            showSuggestionList={true}
            selectedOption={searchType}
            setSelectedOption={setSearchType}
            menuItems={INVOICE_SEARCH_TYPES}
            searchId="invoice-search"
            suggestionsList={invoiceSearchSuggestions}
            onClearSearchInput={onClearSearch}
            onSearchInputChange={onSearchTermChange}
            searchInputvalue={searchText}
            wrapperStyle={`w-38 h-10 cursor-pointer ${!hasPermissionsToView ? 'pointer-events-none' : ''} 
            ${!clicked ? 'lg:flex sm:hidden' : ''} `}
            autoFocus={!!hasPermissionsToView}
          />
          <div className="ml-auto self-end">
            <CustomButton
              id="create-invoice-button"
              onClick={() => handleInvoiceCreation('create-invoice-button')}
              className={'h-[38px] w-[200px] text-primaryBtnText'}
              showDisabledReason={!invoicePermissions.createInvoice || !customerPermissions.createNewCustomers}
              StartIcon={InvoicesTabIcon}
              startIconStyle="path-stroke-current"
              childrenContainerStyles="mr-1">
              <div className="text-sbase font-semibold">Create invoice</div>
            </CustomButton>
          </div>
        </div>

        {hasPermissionsToView && (
          <>
            <InvoiceTabs selectedTab={currentTab} onTabSelect={onTabClick} counts={tabCounts} />

            <div className="my-3 px-6">
              <InvoicesFilter
                onFiltersChange={handleFiltersChange}
                initialFilters={routeStateFiltersRef.current || undefined}
              />
            </div>

            <If condition={invoicesList?.length !== 0}>
              <div className="flex h-10 w-full items-center border-y border-borderGray bg-[#F4F4F4] py-1 px-[30px]">
                <InvoicesTableActions onExportClick={handleExport} onEditTableClick={handleEditTableClick} />
              </div>
            </If>
          </>
        )}

        <div className="mt-top customNormalScroll grow pb-5">
          <If condition={isFetchingInvoices}>
            <div className="flex h-full w-full items-center justify-center">
              <Loader />
            </div>
          </If>
          <If
            condition={
              invoicesList?.length === 0 &&
              !isFetchingInvoices &&
              !invoicesQueryParams.searchValue &&
              hasPermissionsToView &&
              !isFilterResultEmpty &&
              isInvoicesLoadingSuccess
            }>
            <div className="flex h-full flex-col items-center justify-center gap-5">
              <div className="flex flex-col items-center gap-5">
                <InvoicesPageIcon className="h-10 w-10" />
                <div className="text-sbase text-primaryText">Start creating your first invoice</div>
                <CustomButton
                  type={ButtonType.GHOST}
                  id="create-first-invoice-button"
                  onClick={() => handleInvoiceCreation('create-first-invoice-button')}
                  className={`bg-transparent text-sbase font-semibold normal-case 
                  text-secondary`}
                  showDisabledReason={!invoicePermissions.createInvoice || !customerPermissions.createNewCustomers}>
                  Create invoice
                </CustomButton>
              </div>
            </div>
          </If>

          <If condition={!isFetchingInvoices && !hasPermissionsToView}>
            <div className="flex h-full flex-col items-center justify-center gap-5">
              <NoPermissions />
            </div>
          </If>

          <If
            condition={
              invoicesList?.length === 0 &&
              !isFetchingInvoices &&
              invoicesQueryParams.searchValue &&
              invoicesQueryParams.searchValue !== '' &&
              hasPermissionsToView
            }>
            <div className="flex h-full flex-col items-center justify-center gap-5">
              <div className="flex flex-col items-center gap-5">
                <NoSearchResultsIcon />
                <div className="text-sbase text-primaryText">No results found!</div>
              </div>
            </div>
          </If>

          <If condition={isFilterResultEmpty && hasPermissionsToView}>
            <div className="flex h-full flex-col items-center justify-center gap-5">
              <div className="flex flex-col items-center gap-5">
                <InvoicesPageIcon className="h-10 w-10" />
                <div className="text-sbase text-primaryText">No invoices found for selected filters</div>
              </div>
            </div>
          </If>

          <If condition={invoicesList?.length !== 0 && !isFetchingInvoices}>
            <InvoicesTableV2
              invoicesList={invoicesList}
              onInvoiceClick={handleInvoiceClick}
              columnInfo={columnConfig}
              onSortChange={handleSortChangeWrapper}
              currentSortBy={invoicesQueryParams.sortBy}
              currentSortOrder={invoicesQueryParams.sortOrder}
              stickyHorizontalScrollbar={{ bottomOffsetPx: 48 }}
            />
          </If>
        </div>
        <If condition={invoicesList?.length > 0}>
          <Pagination
            currentPage={page}
            totalCount={invoicesCount}
            onPageChange={handlePageChange}
            limit={invoicesQueryParams.limit ?? initialPageSize}
            wrapperClassName="sticky bottom-0 w-full !h-12 py-2.5"
            showSelectResultsPerPage={true}
            onSelectResultsPerPage={handleSelectResultsPerPage}
          />
        </If>
      </div>
      <Outlet context={{ handleSidePanelClose, resetInvoicesQuery }} />
      {showEditTableModal && (
        <CustomizeColumnsModal
          open={showEditTableModal}
          onClose={handleEditTableModalClose}
          onSave={columns => handleEditTableSave(columns as InvoiceV2ColumnInfo[])}
          isLoading={isSavingColumns}
          columns={getInvoicesColumn}
          reorderableStartIndex={2}
        />
      )}
      {invoiceShareModalInfo?.show && (
        <ShareInvoiceModal
          handleClose={handleCloseShareModal}
          show={invoiceShareModalInfo?.show}
          isEditInvoiceShare={invoiceShareModalInfo?.isEditInvoiceShare}
        />
      )}
    </>
  );
};

export default Invoices;
